import { useState, useEffect } from 'preact/hooks';
import { getUsers, getCalendarData, getHolidays } from '../lib/api';

export function WallchartPage({ user, workspace }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState('month'); // 'week', 'fortnight', or 'month'
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDepartments, setExportDepartments] = useState([]);
  const [exportFormat, setExportFormat] = useState('buddy'); // 'buddy' or 'generic'

  useEffect(() => {
    loadData();
  }, [currentDate, viewType]);

  async function loadData() {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRange();

      const [usersData, calendarData, holidaysData] = await Promise.all([
        getUsers(),
        getCalendarData(startDate, endDate),
        getHolidays(currentDate.getFullYear())
      ]);

      setUsers(usersData || []);
      setLeaves(calendarData.leaves || []);
      setHolidays(holidaysData || []);
    } catch (error) {
      console.error('Failed to load wallchart data:', error);
    } finally {
      setLoading(false);
    }
  }

  function getDateRange() {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (viewType === 'week') {
      const day = currentDate.getDay();
      const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(currentDate);
      monday.setDate(diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return {
        startDate: monday.toISOString().split('T')[0],
        endDate: sunday.toISOString().split('T')[0]
      };
    }

    if (viewType === 'fortnight') {
      const day = currentDate.getDay();
      const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(currentDate);
      monday.setDate(diff);
      const endDate = new Date(monday);
      endDate.setDate(monday.getDate() + 13); // 2 weeks = 14 days - 1
      return {
        startDate: monday.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };
    }

    // Month view
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    };
  }

  function getDatesArray() {
    const { startDate, endDate } = getDateRange();
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }

  function navigate(direction) {
    const newDate = new Date(currentDate);
    if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewType === 'fortnight') {
      newDate.setDate(newDate.getDate() + (direction * 14));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  }

  function goToToday() {
    setCurrentDate(new Date());
  }

  function getUserLeaveForDate(userId, dateStr) {
    return leaves.find(leave =>
      leave.user_id === userId &&
      dateStr >= leave.start_date &&
      dateStr <= leave.end_date &&
      ['approved', 'pending'].includes(leave.status)
    );
  }

  function isHoliday(dateStr) {
    return holidays.find(h => h.date === dateStr);
  }

  function isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  function isToday(date) {
    const today = new Date();
    return date.toISOString().split('T')[0] === today.toISOString().split('T')[0];
  }

  function getLeaveColor(leaveType) {
    const colors = {
      'Vacation': '#8B5CF6',
      'Vacation Leave': '#8B5CF6',
      'Sick': '#EF4444',
      'Sick Leave': '#EF4444',
      'Sick day': '#EF4444',
      'Maternity': '#EC4899',
      'Maternity Leave': '#EC4899'
    };
    return colors[leaveType] || '#6B7280';
  }

  function isPartialDay(leave) {
    // Leave is partial if it's hours-based or has specific times set or duration < 1 day
    const isHoursBased = leave.leave_type_unit === 'hours';
    const hasTimes = leave.start_time && leave.end_time;
    const isPartialDuration = leave.duration_days && leave.duration_days < 1;
    return isHoursBased || hasTimes || isPartialDuration;
  }

  function formatLeaveDuration(leave) {
    const unit = leave.leave_type_unit || 'days';
    const val = leave.duration_days;
    if (!val) return '';

    const plural = val !== 1 ? 's' : '';
    if (unit === 'hours') return `${val}h`;
    if (unit === 'weeks') return `${val} week${plural}`;
    if (unit === 'months') return `${val} month${plural}`;
    return `${val} day${plural}`;
  }

  function getShortDuration(leave) {
    const unit = leave.leave_type_unit || 'days';
    const val = leave.duration_days;
    if (!val) return null;
    if (unit === 'hours') return `${val}h`;
    return null; // Only show for hours
  }

  function getLeaveTooltip(leave) {
    let tooltip = `${leave.leave_type} - ${leave.user_name}`;
    const duration = formatLeaveDuration(leave);
    if (duration) {
      tooltip += ` (${duration})`;
    }
    if (leave.start_time && leave.end_time) {
      tooltip += ` ${leave.start_time} - ${leave.end_time}`;
    }
    return tooltip;
  }

  // Open export modal
  function openExportModal() {
    // Default to all departments selected
    const allDeptIds = departments.map(d => d.id);
    setExportDepartments(allDeptIds);
    setShowExportModal(true);
  }

  // Toggle department selection
  function toggleDepartment(deptId) {
    setExportDepartments(prev => {
      if (prev.includes(deptId)) {
        return prev.filter(id => id !== deptId);
      } else {
        return [...prev, deptId];
      }
    });
  }

  // Select all departments
  function selectAllDepartments() {
    setExportDepartments(departments.map(d => d.id));
  }

  // Clear department selection
  function clearDepartments() {
    setExportDepartments([]);
  }

  // Export leave data to CSV for accountants
  function exportLeaveData() {
    const { startDate, endDate } = getDateRange();

    // Filter users by selected departments
    const usersToExport = users.filter(u => {
      if (exportDepartments.length === 0) return true; // All if none selected
      return exportDepartments.includes(u.department_id);
    });

    // Group leaves by user
    const userLeaves = {};

    usersToExport.forEach(u => {
      userLeaves[u.id] = {
        id: u.slack_id || u.id, // Employee ID for Buddy
        name: u.name,
        department: u.department_name || '',
        leaves: []
      };
    });

    // Collect all leaves for each user within the date range
    leaves.forEach(leave => {
      if (!userLeaves[leave.user_id]) return;
      if (leave.status !== 'approved' && leave.status !== 'pending') return;

      // Get the overlapping period with our date range
      const leaveStart = leave.start_date > startDate ? leave.start_date : startDate;
      const leaveEnd = leave.end_date < endDate ? leave.end_date : endDate;

      if (leaveStart > endDate || leaveEnd < startDate) return;

      // Calculate hours for the leave
      const unit = leave.leave_type_unit || 'days';
      let hoursPerDay = 8; // Default 8 hours per day
      let totalHours;

      if (unit === 'hours') {
        totalHours = leave.duration_days || 0;
      } else {
        totalHours = (leave.duration_days || 1) * hoursPerDay;
      }

      // Get dates within range
      const leaveDates = [];
      const current = new Date(leaveStart);
      const end = new Date(leaveEnd);
      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0];
        // Skip weekends and holidays
        const dayOfWeek = current.getDay();
        const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
        const isHolidayDay = holidays.find(h => h.date === dateStr);
        if (!isWeekendDay && !isHolidayDay) {
          leaveDates.push(dateStr);
        }
        current.setDate(current.getDate() + 1);
      }

      // Calculate hours per day within this range
      const workingDays = leaveDates.length;
      const hoursPerWorkingDay = workingDays > 0 ? totalHours / workingDays : totalHours;

      userLeaves[leave.user_id].leaves.push({
        leaveType: leave.leave_type,
        status: leave.status,
        startDate: leaveStart,
        endDate: leaveEnd,
        totalHours,
        dates: leaveDates,
        hoursPerDay: hoursPerWorkingDay,
        createdAt: leave.created_at || startDate // Application date
      });
    });

    let csvContent;
    let filename;

    if (exportFormat === 'buddy') {
      // Buddy payroll format
      // Columns: Employee, Name, Leave Type, Status, Date From, Date To, Application Date, Hours, Already Paid, Statutory, Occupational
      const csvRows = [];
      csvRows.push(['Employee', 'Name', 'Leave Type', 'Status', 'Date From', 'Date To', 'Application Date', 'Hours', 'Already Paid', 'Statutory', 'Occupational'].join(','));

      Object.values(userLeaves).forEach(userData => {
        if (userData.leaves.length === 0) return;

        userData.leaves.forEach(leave => {
          // Convert dates to DD/MM/YYYY format for Buddy
          const formatDate = (dateStr) => {
            const [y, m, d] = dateStr.split('-');
            return `${d}/${m}/${y}`;
          };

          // Determine if statutory or occupational based on leave type
          const isSick = leave.leaveType?.toLowerCase().includes('sick');
          const isMaternity = leave.leaveType?.toLowerCase().includes('maternity');
          const statutory = (isSick || isMaternity) ? 'Yes' : '';
          const occupational = '';

          csvRows.push([
            `"${userData.id}"`,
            `"${userData.name}"`,
            `"${leave.leaveType}"`,
            leave.status === 'approved' ? 'Approved' : 'Pending',
            formatDate(leave.startDate),
            formatDate(leave.endDate),
            formatDate(leave.createdAt?.split('T')[0] || leave.startDate),
            leave.totalHours.toFixed(1),
            '', // Already Paid - blank
            statutory,
            occupational
          ].join(','));
        });
      });

      csvContent = csvRows.join('\n');
      filename = `buddy-leave-import-${startDate}-to-${endDate}.csv`;
    } else {
      // Generic format with more detail
      const csvRows = [];

      // Header info
      csvRows.push(['Report Period', `${startDate} to ${endDate}`].join(','));
      csvRows.push(['Generated', new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })].join(','));
      if (exportDepartments.length > 0 && exportDepartments.length < departments.length) {
        const deptNames = departments.filter(d => exportDepartments.includes(d.id)).map(d => d.name);
        csvRows.push(['Departments', deptNames.join(', ')].join(','));
      }
      csvRows.push('');
      csvRows.push(['Name', 'Department', 'Leave Type', 'Status', 'From Date', 'To Date', 'Total Hours', 'Dates with Hours'].join(','));

      // Data rows
      Object.values(userLeaves).forEach(userData => {
        if (userData.leaves.length === 0) return;

        userData.leaves.forEach(leave => {
          const datesWithHours = leave.dates.map(d =>
            `${d}: ${leave.hoursPerDay.toFixed(1)}h`
          ).join('; ');

          csvRows.push([
            `"${userData.name}"`,
            `"${userData.department}"`,
            `"${leave.leaveType}"`,
            leave.status,
            leave.startDate,
            leave.endDate,
            leave.totalHours.toFixed(1),
            `"${datesWithHours}"`
          ].join(','));
        });
      });

      // Summary section
      csvRows.push('');
      csvRows.push(['Summary by Employee'].join(','));
      csvRows.push(['Name', 'Department', 'Total Hours'].join(','));

      Object.values(userLeaves).forEach(userData => {
        if (userData.leaves.length === 0) return;
        const totalHours = userData.leaves.reduce((sum, l) => sum + l.totalHours, 0);
        csvRows.push([
          `"${userData.name}"`,
          `"${userData.department}"`,
          totalHours.toFixed(1)
        ].join(','));
      });

      csvContent = csvRows.join('\n');
      filename = `leave-report-${startDate}-to-${endDate}.csv`;
    }

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setShowExportModal(false);
  }

  // Filter users
  const filteredUsers = users.filter(u => {
    if (departmentFilter !== 'all' && u.department_id !== departmentFilter) return false;
    if (locationFilter !== 'all' && u.location_id !== locationFilter) return false;
    return true;
  });

  // Get unique departments and locations
  const departments = [...new Set(users.filter(u => u.department_name).map(u => ({ id: u.department_id, name: u.department_name })))];
  const locations = [...new Set(users.filter(u => u.location_name).map(u => ({ id: u.location_id, name: u.location_name })))];

  const dates = getDatesArray();
  const today = new Date().toISOString().split('T')[0];

  const periodLabel = viewType === 'week' || viewType === 'fortnight'
    ? `${viewType === 'fortnight' ? 'Fortnight' : 'Week'} of ${dates[0]?.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} - ${dates[dates.length - 1]?.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div class="loading-screen">
        <div class="spinner" />
      </div>
    );
  }

  return (
    <div class="wallchart-page">
      {/* Controls */}
      <div class="wallchart-controls">
        <div class="wallchart-nav">
          <button class="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
            &lt; Previous
          </button>
          <button class="btn btn-secondary btn-sm" onClick={goToToday}>
            Today
          </button>
          <span class="wallchart-period">{periodLabel}</span>
          <button class="btn btn-secondary btn-sm" onClick={() => navigate(1)}>
            Next &gt;
          </button>
        </div>

        <div class="wallchart-filters">
          <div class="view-toggle">
            <button
              class={`toggle-btn ${viewType === 'week' ? 'active' : ''}`}
              onClick={() => setViewType('week')}
            >
              Week
            </button>
            <button
              class={`toggle-btn ${viewType === 'fortnight' ? 'active' : ''}`}
              onClick={() => setViewType('fortnight')}
            >
              Fortnight
            </button>
            <button
              class={`toggle-btn ${viewType === 'month' ? 'active' : ''}`}
              onClick={() => setViewType('month')}
            >
              Month
            </button>
          </div>

          {departments.length > 0 && (
            <select
              class="form-select form-select-sm"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="all">All Departments</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          )}

          {locations.length > 0 && (
            <select
              class="form-select form-select-sm"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="all">All Locations</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}

          <button class="btn btn-secondary btn-sm export-btn" onClick={openExportModal} title="Export leave data to CSV">
            Export CSV
          </button>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div class="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div class="modal export-modal" onClick={(e) => e.stopPropagation()}>
            <div class="modal-header">
              <h3>Export Leave Data</h3>
              <button class="modal-close" onClick={() => setShowExportModal(false)}>&times;</button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label>Export Format</label>
                <div class="format-options">
                  <label class="radio-label buddy-option">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="buddy"
                      checked={exportFormat === 'buddy'}
                      onChange={() => setExportFormat('buddy')}
                    />
                    <div class="format-logo buddy-logo">
                      {/* Buddy HR Red Panda Logo */}
                      <svg viewBox="0 0 100 100" width="36" height="36">
                        {/* Ears */}
                        <ellipse cx="25" cy="25" rx="18" ry="20" fill="#E86B1A"/>
                        <ellipse cx="75" cy="25" rx="18" ry="20" fill="#E86B1A"/>
                        <ellipse cx="25" cy="25" rx="10" ry="12" fill="#4A1F0D"/>
                        <ellipse cx="75" cy="25" rx="10" ry="12" fill="#4A1F0D"/>
                        {/* Face */}
                        <ellipse cx="50" cy="55" rx="40" ry="38" fill="#E86B1A"/>
                        {/* White face markings */}
                        <ellipse cx="35" cy="50" rx="15" ry="18" fill="#FFF"/>
                        <ellipse cx="65" cy="50" rx="15" ry="18" fill="#FFF"/>
                        <ellipse cx="50" cy="70" rx="12" ry="10" fill="#FFF"/>
                        {/* Eyes */}
                        <circle cx="35" cy="48" r="4" fill="#4A1F0D"/>
                        <circle cx="65" cy="48" r="4" fill="#4A1F0D"/>
                        {/* Nose */}
                        <ellipse cx="50" cy="62" rx="6" ry="5" fill="#4A1F0D"/>
                        {/* Mouth */}
                        <path d="M44 70 Q50 76 56 70" stroke="#4A1F0D" stroke-width="2" fill="none"/>
                      </svg>
                    </div>
                    <div class="format-text">
                      <span>Buddy Payroll</span>
                      <small>Compatible with Buddy HR import</small>
                    </div>
                  </label>
                  <label class="radio-label">
                    <input
                      type="radio"
                      name="exportFormat"
                      value="generic"
                      checked={exportFormat === 'generic'}
                      onChange={() => setExportFormat('generic')}
                    />
                    <div class="format-logo generic-logo">
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                      </svg>
                    </div>
                    <div class="format-text">
                      <span>Generic CSV</span>
                      <small>Detailed report with date breakdown</small>
                    </div>
                  </label>
                </div>
              </div>

              <div class="form-group">
                <label>
                  Select Departments
                  <span class="dept-actions">
                    <button type="button" class="btn-link" onClick={selectAllDepartments}>Select All</button>
                    <button type="button" class="btn-link" onClick={clearDepartments}>Clear</button>
                  </span>
                </label>
                {departments.length > 0 ? (
                  <div class="department-checkboxes">
                    {departments.map(d => (
                      <label key={d.id} class="checkbox-label">
                        <input
                          type="checkbox"
                          checked={exportDepartments.includes(d.id)}
                          onChange={() => toggleDepartment(d.id)}
                        />
                        <span>{d.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p class="text-muted">No departments configured. All users will be exported.</p>
                )}
              </div>

              <div class="export-info">
                <p>Exporting data for: <strong>{periodLabel}</strong></p>
                <p>
                  {exportDepartments.length === 0 || exportDepartments.length === departments.length
                    ? 'All departments'
                    : `${exportDepartments.length} department${exportDepartments.length !== 1 ? 's' : ''} selected`
                  }
                </p>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" onClick={() => setShowExportModal(false)}>Cancel</button>
              <button class="btn btn-primary" onClick={exportLeaveData}>
                Export {exportFormat === 'buddy' ? 'for Buddy' : 'CSV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div class="wallchart-legend">
        <div class="legend-item">
          <span class="legend-dot vacation" />
          <span>Vacation</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot sick" />
          <span>Sick</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot maternity" />
          <span>Maternity</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot holiday" />
          <span>Holiday</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot pending" />
          <span>Pending</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot partial" />
          <span>Partial Day</span>
        </div>
      </div>

      {/* Wallchart Grid */}
      <div class={`wallchart-container view-${viewType}`}>
        <div class="wallchart-grid">
          {/* Header Row */}
          <div class="wallchart-header">
            <div class="wallchart-user-col">Team Member</div>
            {dates.map(date => {
              const dateStr = date.toISOString().split('T')[0];
              const holiday = isHoliday(dateStr);
              const weekend = isWeekend(date);
              const todayClass = isToday(date) ? 'today' : '';

              return (
                <div
                  key={dateStr}
                  class={`wallchart-date-col ${weekend ? 'weekend' : ''} ${todayClass} ${holiday ? 'holiday' : ''}`}
                  title={holiday ? holiday.name : undefined}
                >
                  <div class="date-weekday">{date.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                  <div class="date-day">{date.getDate()}</div>
                </div>
              );
            })}
          </div>

          {/* User Rows */}
          {filteredUsers.length > 0 ? (
            filteredUsers.map(u => (
              <div key={u.id} class="wallchart-row">
                <div class="wallchart-user-col">
                  <div class="wallchart-user">
                    <div class="avatar">
                      {u.avatar_url ? <img src={u.avatar_url} alt="" /> : u.name?.charAt(0)}
                    </div>
                    <div class="user-details">
                      <div class="user-name">{u.name}</div>
                      {u.department_name && (
                        <div class="user-dept">{u.department_name}</div>
                      )}
                    </div>
                  </div>
                </div>
                {dates.map(date => {
                  const dateStr = date.toISOString().split('T')[0];
                  const leave = getUserLeaveForDate(u.id, dateStr);
                  const holiday = isHoliday(dateStr);
                  const weekend = isWeekend(date);
                  const todayClass = isToday(date) ? 'today' : '';
                  const partial = leave && isPartialDay(leave);

                  let cellClass = `wallchart-cell ${weekend ? 'weekend' : ''} ${todayClass}`;
                  let cellStyle = {};
                  let cellContent = null;

                  if (holiday) {
                    cellClass += ' holiday';
                  } else if (leave) {
                    cellClass += ` has-leave ${leave.status}`;
                    if (partial) {
                      // Partial day: show diagonal stripe pattern
                      cellClass += ' partial-day';
                      const color = getLeaveColor(leave.leave_type);
                      cellStyle.background = `repeating-linear-gradient(
                        -45deg,
                        ${color},
                        ${color} 3px,
                        transparent 3px,
                        transparent 6px
                      )`;
                    } else {
                      cellStyle.backgroundColor = getLeaveColor(leave.leave_type);
                    }
                    if (leave.status === 'pending') {
                      cellClass += ' pending-leave';
                    }
                    const shortDuration = getShortDuration(leave);
                    cellContent = (
                      <div class="leave-indicator" title={getLeaveTooltip(leave)}>
                        {shortDuration || (partial ? '½' : (leave.icon || leave.leave_type?.charAt(0)))}
                      </div>
                    );
                  }

                  return (
                    <div key={dateStr} class={cellClass} style={cellStyle}>
                      {cellContent}
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            <div class="wallchart-empty">
              <p>No team members found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
